import { useEffect, useState, useCallback } from "react";
import { useAuth, useUser } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { toast } from "sonner";
import { format } from "date-fns";

const REASON_COLORS = ["#F97316", "#DC2626", "#D97706", "#8B5CF6", "#0284C7", "#059669", "#64748B", "#EAB308"];

export default function WastePage() {
  const { locationId } = useAuth();
  const user = useUser();
  const { t } = useLang();
  const [products, setProducts] = useState([]);
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState(null);
  const [form, setForm] = useState({ product_id: "", quantity: "", reason: "scadenza", shift: "", note: "" });
  const [busy, setBusy] = useState(false);
  const canSeeCosts = ["owner", "manager", "chef"].includes(user?.role);

  const load = useCallback(() => {
    const params = { location_id: locationId };
    api.get("/products", { params }).then((r) => setProducts(r.data.filter((p) => p.current_qty > 0))).catch(() => {});
    api.get("/waste", { params }).then((r) => setRecords(r.data)).catch(() => {});
    api.get("/waste/stats", { params }).then((r) => setStats(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [locationId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener("sk:data-changed", h);
    return () => window.removeEventListener("sk:data-changed", h);
  }, [load]);

  const selected = products.find((p) => p.product_id === form.product_id);
  const estimated = selected && form.quantity ? (selected.avg_cost || 0) * Number(form.quantity) : 0;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/waste", { ...form, quantity: Number(form.quantity), location_id: locationId });
      toast.success(t("common.success"));
      setForm({ product_id: "", quantity: "", reason: "scadenza", shift: "", note: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };

  const chartData = stats ? Object.entries(stats.by_reason).map(([reason, value], i) => ({
    name: t(`waste.reasons.${reason}`), value, fill: REASON_COLORS[i % REASON_COLORS.length],
  })) : [];

  return (
    <div className="space-y-6 md:pl-14" data-testid="waste-page">
      <h1 className="font-display text-2xl sm:text-3xl font-extrabold">{t("waste.title")}</h1>

      {stats && canSeeCosts && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="waste-stats">
          {[
            { label: t("waste.today"), value: stats.today, tid: "waste-stat-today" },
            { label: t("waste.week"), value: stats.week, tid: "waste-stat-week" },
            { label: t("waste.month"), value: stats.month, tid: "waste-stat-month" },
            { label: t("waste.year"), value: stats.year, tid: "waste-stat-year" },
          ].map((s) => (
            <div key={s.tid} data-testid={s.tid} className="bg-card border rounded-2xl p-4">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{s.label}</p>
              <p className="font-display text-2xl font-extrabold text-orange-700 mt-1">€{s.value.toFixed(2)}</p>
            </div>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-5">
        <form onSubmit={submit} className="bg-card border rounded-2xl p-5 space-y-4" data-testid="waste-form">
          <h2 className="font-display font-bold flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-orange-600" />{t("waste.log")}
          </h2>
          <div className="space-y-1.5">
            <Label>{t("waste.product")}</Label>
            <select data-testid="waste-product-select" required value={form.product_id}
              onChange={(e) => setForm({ ...form, product_id: e.target.value })}
              className="h-12 w-full rounded-md border bg-background px-3 text-sm">
              <option value="">—</option>
              {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name} ({p.current_qty} {p.unit})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("products.quantity")}{selected ? ` (${selected.unit})` : ""}</Label>
              <Input data-testid="waste-quantity" type="number" step="0.01" min="0.01" required
                value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="h-12" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("waste.reason")}</Label>
              <select data-testid="waste-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}
                className="h-12 w-full rounded-md border bg-background px-3 text-sm">
                {Object.keys(t("waste.reasons")).map((r) => <option key={r} value={r}>{t(`waste.reasons.${r}`)}</option>)}
              </select>
            </div>
          </div>
          {canSeeCosts && estimated > 0 && (
            <p className="text-sm font-mono font-bold text-orange-700" data-testid="waste-estimated-cost">
              {t("waste.value")}: €{estimated.toFixed(2)}
            </p>
          )}
          <Button data-testid="waste-log-submit-button" type="submit" disabled={busy || !form.product_id}
            className="w-full h-12 bg-orange-600 hover:bg-orange-700 tactile-btn font-bold">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("waste.log")}
          </Button>
        </form>

        {canSeeCosts && chartData.length > 0 && (
          <div className="bg-card border rounded-2xl p-5" data-testid="waste-chart">
            <h2 className="font-display font-bold mb-3">{t("waste.byReason")}</h2>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 8, right: 16 }}>
                <XAxis type="number" hide />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `€${Number(v).toFixed(2)}`} />
                <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                  {chartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-card border rounded-2xl p-5" data-testid="waste-history">
        <h2 className="font-display font-bold mb-3">{t("waste.history")}</h2>
        <div className="space-y-2">
          {records.map((r) => (
            <div key={r.waste_id} data-testid={`waste-record-${r.waste_id}`}
              className="flex items-center gap-3 flex-wrap border-b last:border-0 pb-2">
              <div className="flex-1 min-w-32">
                <p className="font-semibold text-sm">{r.product_name}</p>
                <p className="text-xs text-muted-foreground">
                  {format(new Date(r.created_at), "dd/MM/yyyy HH:mm")} · {r.user_name}
                </p>
              </div>
              <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-md bg-orange-100 text-orange-800">
                {t(`waste.reasons.${r.reason}`)}
              </span>
              <p className="font-mono text-sm font-bold">{r.quantity} {r.unit}</p>
              {canSeeCosts && <p className="font-mono text-sm text-orange-700 font-bold">€{r.cost.toFixed(2)}</p>}
              <button data-testid={`waste-delete-${r.waste_id}`} title={t("common.delete")}
                onClick={async () => {
                  if (!window.confirm(t("common.confirmDelete"))) return;
                  try { await api.delete(`/waste/${r.waste_id}`); toast.success(t("common.success")); load(); }
                  catch (e) { toast.error(formatApiError(e)); }
                }}
                className="tactile-btn h-9 w-9 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-red-600 shrink-0">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {!records.length && <p className="text-sm text-muted-foreground">{t("notif.empty")}</p>}
        </div>
      </div>
    </div>
  );
}
